/**
 * brand/mark.ts — the Ticketless mark. Its geometry and its two palettes are stated HERE and nowhere else.
 *
 * Four things draw this mark: the app bar (`Mark.tsx`), the browser tab (`favicon.ts`), the committed
 * static assets (`scripts/generate-icons.ts`), and the README. A mark restated per consumer drifts — a
 * sibling application shipped `--logo-start`/`--logo-end` tokens that NOTHING read, because its component
 * held two hex literals of its own, so every livery wore the default badge and the favicon matched none of
 * them. The lesson taken here is not "declare tokens", it is "declare the art once and derive".
 *
 * Consequently this module holds no DOM and no React: it is imported by a browser bundle, by a jsdom test
 * and by a Node script, and anything platform-specific here would break one of the three.
 *
 * **The glyph.** Three bulleted rows, ragged right, shortening downwards — a changelog entry with the
 * trivia already dropped, which is precisely what the application produces. The raggedness is the
 * signature: an even three-bar glyph is a hamburger menu, and the leading dots are what make it a list
 * somebody wrote rather than a control somebody clicks.
 *
 * **Two palettes, not one.** The badge is dark-on-light and light-on-dark, because a single badge tuned for
 * one theme is washed out or scorching in the other. `mark.test.ts` walks each gradient at 1% and asserts
 * the ink clears 3:1 against every point on it — logotypes are exempt from WCAG's 4.5:1, but exempt from
 * the text floor is not exempt from being legible. The walk exists because sRGB interpolation is not
 * luminance interpolation: the worst point on a gradient is often between the stops, not at one.
 */

/** The design grid. Every number below is in these units; nothing here knows about pixels. */
export const MARK_GRID = 24;

/** The badge's corner radius, in grid units — ~23%, the modern app-icon proportion. */
export const MARK_BADGE_RADIUS = 5.5;

interface MarkRow {
  /** Vertical centre of the row. */
  readonly cy: number;
  /** Right-hand end of the row's bar. Shortening down the rows is the "and less, and less" signature. */
  readonly barEnd: number;
}

const ROWS: readonly MarkRow[] = [
  { cy: 7, barEnd: 18.35 },
  { cy: 12, barEnd: 16.3 },
  { cy: 17, barEnd: 14 },
];

const DOT_CX = 7.15;
const DOT_RADIUS = 1.4;
const BAR_X = 10.3;
const BAR_HEIGHT = 2.4;

export interface MarkDot {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

export interface MarkBar {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly radius: number;
}

/** Rounded to four places: `18.35 - 10.3` is `8.049999999999999` in binary floating point, and a mark
 *  whose serialisation depends on the last bit of a subtraction is a mark that cannot be compared. */
function grid(value: number): number {
  return Math.round(value * 10000) / 10000;
}

export const MARK_DOTS: readonly MarkDot[] = ROWS.map((row) => ({
  cx: DOT_CX,
  cy: row.cy,
  r: DOT_RADIUS,
}));

export const MARK_BARS: readonly MarkBar[] = ROWS.map((row) => ({
  x: BAR_X,
  y: grid(row.cy - BAR_HEIGHT / 2),
  width: grid(row.barEnd - BAR_X),
  height: BAR_HEIGHT,
  radius: BAR_HEIGHT / 2,
}));

export type MarkTheme = "light" | "dark";

export interface MarkPalette {
  /** Top-left gradient stop. */
  readonly start: string;
  /** Bottom-right gradient stop. */
  readonly end: string;
  /** The glyph. */
  readonly ink: string;
}

/**
 * The two liveries. Mirrored into `styles.css` as `--logo-start` / `--logo-end` / `--logo-ink` for the
 * in-app SVG, whose gradient stops cannot take a `var()` in a presentation attribute and so are styled by
 * class instead. `mark.test.ts` reads that stylesheet and asserts the mirror still matches — the copy is
 * unavoidable, so it is made loud rather than trusted.
 */
export const MARK_PALETTE: Readonly<Record<MarkTheme, MarkPalette>> = {
  light: { start: "#10858e", end: "#0a565d", ink: "#ffffff" },
  dark: { start: "#3ab8c2", end: "#2596a1", ink: "#08252a" },
};

/** The gradient's id inside a standalone SVG document. In-page instances mint their own — see `Mark.tsx`. */
const GRADIENT_ID = "ticketless-mark";

function badge(gradientId: string): string {
  return (
    `<rect width="${MARK_GRID}" height="${MARK_GRID}" rx="${MARK_BADGE_RADIUS}"` +
    ` fill="url(#${gradientId})"/>`
  );
}

function glyph(fill: string): string {
  const dots = MARK_DOTS.map((d) => `<circle cx="${d.cx}" cy="${d.cy}" r="${d.r}"/>`).join("");
  const bars = MARK_BARS.map(
    (b) => `<rect x="${b.x}" y="${b.y}" width="${b.width}" height="${b.height}" rx="${b.radius}"/>`,
  ).join("");
  return `<g fill="${fill}">${dots}${bars}</g>`;
}

function open(): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${MARK_GRID} ${MARK_GRID}"` +
    ` width="${MARK_GRID}" height="${MARK_GRID}">`
  );
}

/**
 * The mark as a standalone SVG document in ONE livery, with literal colours.
 *
 * This is what `favicon.ts` bakes into a `data:` URI. A favicon is a separate document and cannot read the
 * page's custom properties, so the colours must be resolved before they are written — which is exactly why
 * the runtime path resolves the tokens from the cascade and hands them here rather than guessing a theme.
 */
export function markSvg(palette: MarkPalette): string {
  return (
    open() +
    `<defs><linearGradient id="${GRADIENT_ID}" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="${palette.start}"/>` +
    `<stop offset="1" stop-color="${palette.end}"/>` +
    `</linearGradient></defs>` +
    badge(GRADIENT_ID) +
    glyph(palette.ink) +
    `</svg>`
  );
}

/**
 * The mark as a standalone SVG document carrying BOTH liveries, switched by `prefers-color-scheme`.
 *
 * This is the committed `favicon.svg`, and it is what a browser shows before any script has run — so it
 * follows the operating system, which is the right default. Once the bundle executes, `favicon.ts` replaces
 * it with a resolved single-livery data URI, because only the page knows whether the viewer has overridden
 * the operating system with the theme toggle.
 */
export function markSvgThemed(): string {
  const { light, dark } = MARK_PALETTE;
  const style =
    `<style>` +
    `.s0{stop-color:${light.start}}.s1{stop-color:${light.end}}.ink{fill:${light.ink}}` +
    `@media(prefers-color-scheme:dark){` +
    `.s0{stop-color:${dark.start}}.s1{stop-color:${dark.end}}.ink{fill:${dark.ink}}}` +
    `</style>`;
  const dots = MARK_DOTS.map((d) => `<circle cx="${d.cx}" cy="${d.cy}" r="${d.r}"/>`).join("");
  const bars = MARK_BARS.map(
    (b) => `<rect x="${b.x}" y="${b.y}" width="${b.width}" height="${b.height}" rx="${b.radius}"/>`,
  ).join("");
  return (
    open() +
    style +
    `<defs><linearGradient id="${GRADIENT_ID}" x1="0" y1="0" x2="1" y2="1">` +
    `<stop class="s0" offset="0"/><stop class="s1" offset="1"/>` +
    `</linearGradient></defs>` +
    badge(GRADIENT_ID) +
    `<g class="ink">${dots}${bars}</g>` +
    `</svg>`
  );
}
